<template>
  <div class="listings-filters-content-ui">
    <VTabs :tabs="tabs" toggle-manually swipeable>
      <template #tab-0="{ active }">
        <div v-if="active" class="listings-filters-content-ui__options">
          <div class="listings-filters-content-ui__options-item">
            <ListingsPriceFilter v-model="filters.price_range" :sale-type="filters.type" />
          </div>
          <div class="listings-filters-content-ui__options-item">
            <ListingsBedsFilter v-model="filters.bed_range" />
          </div>
          <div class="listings-filters-content-ui__options-item">
            <ListingsTypeFilter v-model="filters.category" />
          </div>
          <div class="listings-filters-content-ui__options-item">
            <ListingsDateFilter v-model="filters.date" :label="dateFieldLabel" />
          </div>
          <div class="listings-filters-content-ui__options-item">
            <HiddenOpportunitiesFilter v-model="filters.hidden" />
          </div>
          <div class="listings-filters-content-ui__options-item">
            <ListingsCrmActivitySinceFilter v-model="filters.exclude_crm_activity_since" />
          </div>
          <div class="listings-filters-content-ui__options-item">
            <ExcludeSoldSinceFilter v-model="excludeSoldSince" />
          </div>
          <div v-if="isExternalCompany" class="listings-filters-content-ui__options-item">
            <ListingsContactableOnlyFilter v-model="contactableOnly" />
          </div>
          <!-- Filters only for for the on-market feature -->
          <div v-if="isOnMarketFeature" class="listings-filters-content-ui__options-item">
            <ListingsUnactionedOnlyFilter v-model="unactionedOnly" />
          </div>
        </div>
      </template>
      <template v-if="isExternalCompany" #tab-1="{ active }">
        <keep-alive>
          <ListingsFiltersData
            v-if="active"
            v-model="filters"
            :opportunity-data-client="opportunityDataClient"
          />
        </keep-alive>
      </template>
      <template v-if="isExternalCompany" #tab-2="{ active }">
        <keep-alive>
          <ListingsFiltersMarketingConsent v-if="active" v-model="filters" />
        </keep-alive>
      </template>
    </VTabs>
    <div class="listings-filters-content-ui__actions">
      <div class="listings-filters-content-ui__clear-action">
        <VBtn flat @click="$emit('clear')">Clear all</VBtn>
        <VIconTooltip v-if="showClearTooltip" class="ghost" :icon-src="infoIcon">
          Resets all filters to company defaults
        </VIconTooltip>
      </div>
      <VBtn class="full-width" :disabled="isApplyDisabled" @click="$emit('apply')">{{
        applyButtonLabel
      }}</VBtn>
    </div>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { TListingFilters, TOpportunityDataClientModel } from '~/misc/listings/types';
  import type { ITab } from '~/misc/v-tabs/types';
  import type { AbstractDataClient } from '~/misc/services/data-client';
  import { EOpportunitySearch } from '~/misc/listings/types';

  // Components
  import ListingsPriceFilter from '~/misc/listings/filters/price/ListingsPriceFilter.vue';
  import ListingsBedsFilter from '~/misc/listings/filters/beds/ListingsBedsFilter.vue';
  import ListingsTypeFilter from '~/misc/listings/filters/type/ListingsTypeFilter.vue';
  import ListingsDateFilter from '~/misc/listings/filters/date/ListingsDateFilter.vue';
  import ExcludeSoldSinceFilter from '~/misc/listings/filters/exclude-sold-since/ExcludeSoldSinceFilter.vue';
  import VBtn from '~/misc/v-btn';
  import VTabs from '~/misc/v-tabs';
  import ListingsFiltersData from '~/misc/listings/filters/data/ListingsFiltersData.vue';
  import ListingsFiltersMarketingConsent from '~/misc/listings/filters/marketing-consent/ListingsFiltersMarketingConsent.vue';
  import ListingsContactableOnlyFilter from '~/misc/listings/filters/contactable-only/ListingsContactableOnlyFilter.vue';
  import HiddenOpportunitiesFilter from '~/misc/listings/filters/hidden-opportunities/HiddenOpportunitiesFilter.vue';
  import ListingsUnactionedOnlyFilter from '~/misc/listings/filters/unactioned-only/ListingsUnactionedOnlyFilter.vue';
  import ListingsCrmActivitySinceFilter from '~/misc/listings/filters/crm-activity-since/ListingsCrmActivitySinceFilter.vue';
  import VIconTooltip from '~/misc/v-icon-tooltip/VIconTooltip.vue';

  // Assets
  import infoIcon from '~/assets/images/icons/info-icon.svg';

  const props = withDefaults(
    defineProps<{
      tabs: ITab[];
      dateFieldLabel?: string;
      applyButtonLabel?: string;
      opportunityDataClient?: AbstractDataClient<TOpportunityDataClientModel>;
      isExternalCompany?: boolean;
      showClearTooltip?: boolean;
      feature?: EOpportunitySearch | null;
    }>(),
    {
      dateFieldLabel: 'Date',
      applyButtonLabel: 'Apply',
      opportunityDataClient: undefined,
      feature: undefined,
    }
  );

  defineEmits<{
    (e: 'apply'): void;
    (e: 'clear'): void;
  }>();

  const filters = defineModel<TListingFilters>({ required: true });

  const excludeSoldSince: WritableComputedRef<boolean> = computed<boolean>({
    get(): boolean {
      // null = no preference → show default (true = exclude)
      return filters.value.exclude_sold_since === null ? true : !!filters.value.exclude_sold_since;
    },
    set(value: boolean): void {
      filters.value.exclude_sold_since = Number(value);
    },
  });

  const contactableOnly: WritableComputedRef<boolean> = computed<boolean>({
    get(): boolean {
      // null = no preference → show default (false = show all)
      return filters.value.contactable_only === null ? false : !!filters.value.contactable_only;
    },
    set(value: boolean): void {
      filters.value.contactable_only = Number(value);
    },
  });

  const unactionedOnly: WritableComputedRef<boolean> = computed<boolean>({
    get(): boolean {
      // null = no preference → show default (false = show all)
      return filters.value.exclude_actioned_properties === null
        ? false
        : !!filters.value.exclude_actioned_properties;
    },
    set(value: boolean): void {
      filters.value.exclude_actioned_properties = Number(value);
    },
  });

  const isOnMarketFeature: ComputedRef<boolean> = computed<boolean>(
    () => props.feature === EOpportunitySearch.ON_MARKET
  );

  const isApplyDisabled: ComputedRef<boolean> = computed<boolean>(() => {
    if (!props.isExternalCompany) return false;

    return filters.value.contact_type.length === 0 || filters.value.marketing_consent.length === 0;
  });
</script>
